import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
  BuildingOffice2Icon,
  EyeIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useDeals } from '../../hooks/useDeals';
import { useCustomers } from '../../hooks/useCustomers';
import { useUsers } from '../../hooks/useUsers';
import { useAuth } from '../../contexts/AuthContext';
import { Deal, DealFormData } from '../../types/deal';
import { Tooltip } from '../common/Tooltip';
import LoadingScreen from '../common/LoadingScreen';
import { formatDistanceToNow, format, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import { hu } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import DealForm from './DealForm';
import SearchBar from '../common/SearchBar';
import Modal from '../common/Modal';
import Pagination from '../common/Pagination';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface DealListProps {
  customerId?: string;
  triggerNewForm?: boolean;
  onFormOpened?: () => void;
}

const DealList: React.FC<DealListProps> = ({ customerId, triggerNewForm, onFormOpened }) => {
  const { deals, loading, addDeal, updateDeal, deleteDeal } = useDeals(customerId);
  const { customers, loading: customersLoading } = useCustomers();
  const { users } = useUsers();
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewDeal, setViewDeal] = useState<Deal | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Date filter states
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [datePreset, setDatePreset] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleTypeFilter = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleTagFilter = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleUserFilter = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(u => u !== userId)
        : [...prev, userId]
    );
  };

  const handleDatePreset = (preset: string) => {
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    switch (preset) {
      case 'today':
        setDateFrom(formatDate(today));
        setDateTo(formatDate(today));
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Hétfő = 0
        startOfWeek.setDate(today.getDate() - daysToMonday);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        setDateFrom(formatDate(startOfWeek));
        setDateTo(formatDate(endOfWeek));
        break;
      case 'lastWeek':
        const lastWeekStart = new Date(today);
        const lastWeekDayOfWeek = today.getDay();
        const daysToLastMonday = lastWeekDayOfWeek === 0 ? 13 : lastWeekDayOfWeek + 6;
        lastWeekStart.setDate(today.getDate() - daysToLastMonday);
        
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        
        setDateFrom(formatDate(lastWeekStart));
        setDateTo(formatDate(lastWeekEnd));
        break;
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        setDateFrom(formatDate(startOfMonth));
        setDateTo(formatDate(endOfMonth));
        break;
      case 'custom':
        // Ne változtassunk semmit, a felhasználó manuálisan állítja be
        break;
      default:
        setDateFrom('');
        setDateTo('');
        break;
    }
    setDatePreset(preset);
  };

  const handleManualDateChange = (type: 'from' | 'to', value: string) => {
    if (type === 'from') {
      setDateFrom(value);
    } else {
      setDateTo(value);
    }
    // Ha manuálisan változtatják a dátumot, állítsuk át "custom"-ra
    if (datePreset !== 'custom') {
      setDatePreset('custom');
    }
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedTags([]);
    setSelectedUsers([]);
    setDateFrom('');
    setDateTo('');
    setDatePreset('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedTypes.length > 0 || selectedStatuses.length > 0 || selectedTags.length > 0 || selectedUsers.length > 0 || dateFrom || dateTo;

  const getCustomerName = (id: string) => {
    const customer = customers?.find(c => c.id === id);
    if (!customer) return 'Unknown Customer';

    return customer.type === 'organization'
      ? customer.companyName
      : `${customer.lastName} ${customer.firstName}`;
  };

  const getCustomerPhone = (id: string) => {
    const customer = customers?.find(c => c.id === id);
    if (!customer) return '';
    return customer.phone || '';
  };

  const getCustomerEmail = (id: string) => {
    const customer = customers?.find(c => c.id === id);
    if (!customer) return '';
    return customer.email || '';
  };

  const getUserName = (id?: string) => {
    if (!id) return t('deals.fields.noAssignedUser');
    
    // Először keressük a felhasználót a teljes listából (már hozzárendelt felhasználók esetén)
    const assignedUser = users?.find(u => u.id === id);
    
    // Ha megtaláltuk a felhasználót
    if (assignedUser) {
      // Ellenőrizzük, hogy a jelenlegi felhasználó láthatja-e
      const canView = currentUser?.role === 'super_admin' || 
                     (currentUser?.organizationId && assignedUser.organizationId === currentUser?.organizationId);
      
      if (canView) {
        return assignedUser.displayName || assignedUser.email;
      } else {
        // Ha nem látható, de létezik, jelezzük hogy más szervezetből van
        return `${assignedUser.displayName || assignedUser.email} (${t('deals.fields.otherOrganization')})`;
      }
    }
    
    return t('deals.fields.noAssignedUser');
  };

  // Szűrt felhasználók a dropdown-hoz
  const availableUsers = users?.filter(user => {
    // Super admin mindig látja az összes felhasználót
    if (currentUser?.role === 'super_admin') return true;
    
    // A bejelentkezett felhasználót mindig megjelenítjük
    if (user.id === currentUser?.id) return true;
    
    // Ha a jelenlegi felhasználónak nincs szervezete, akkor csak önmagát látja
    if (!currentUser?.organizationId) return false;
    
    // Normál felhasználók csak ugyanazon szervezet tagjait látják
    return user.organizationId === currentUser?.organizationId;
  }) || [];

  // Get all available tags from deals
  const getAllTags = () => {
    const tagSet = new Set<string>();
    deals.forEach(deal => {
      if (deal.tags) {
        deal.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  };

  const filteredDeals = useMemo(() => {
    return deals.filter((deal) => {
      const customerName = getCustomerName(deal.customerId) || '';
      const assignedUserName = getUserName(deal.assignedUserId) || '';
      const searchMatch = deal.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (deal.subject && deal.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
        customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignedUserName.toLowerCase().includes(searchTerm.toLowerCase());

      const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(deal.type);
      const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(deal.status);
      const tagMatch = selectedTags.length === 0 || (deal.tags && deal.tags.some(tag => selectedTags.includes(tag)));
      const userMatch = selectedUsers.length === 0 || 
        (deal.assignedUserId && selectedUsers.includes(deal.assignedUserId)) ||
        (selectedUsers.includes('unassigned') && !deal.assignedUserId);

      // Date filtering
      let dateMatch = true;
      if (dateFrom || dateTo) {
        const dealDate = startOfDay(new Date(deal.date));
        
        if (dateFrom && dateTo) {
          const fromDate = startOfDay(new Date(dateFrom));
          const toDate = endOfDay(new Date(dateTo));
          dateMatch = dealDate >= fromDate && dealDate <= toDate;
        } else if (dateFrom) {
          const fromDate = startOfDay(new Date(dateFrom));
          dateMatch = dealDate >= fromDate;
        } else if (dateTo) {
          const toDate = endOfDay(new Date(dateTo));
          dateMatch = dealDate <= toDate;
        }
      }

      return searchMatch && typeMatch && statusMatch && tagMatch && userMatch && dateMatch;
    });
  }, [deals, searchTerm, selectedTypes, selectedStatuses, selectedTags, selectedUsers, dateFrom, dateTo, customers]);

  // Pagination logic
  const totalPages = Math.ceil(filteredDeals.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDeals = filteredDeals.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSubmit = async (data: DealFormData) => {
    try {
      // Combine followUpDate and followUpTime into a single Date object
      let followUpDate: Date | undefined = undefined;
      if (data.followUpDate) {
        followUpDate = new Date(data.followUpDate);
        if (data.followUpTime) {
          const [hours, minutes] = data.followUpTime.split(':');
          followUpDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        }
      }

      if (selectedDeal) {
        await updateDeal(selectedDeal.id, {
          ...data,
          date: new Date(data.date),
          followUpDate
        });
        toast.success(t('deals.messages.updated'));
      } else {
        await addDeal({
          ...data,
          date: new Date(data.date),
          followUpDate
        });
        toast.success(t('deals.messages.created'));
      }
      setIsFormOpen(false);
      setSelectedDeal(null);
    } catch (error) {
      console.error('Error submitting deal:', error);
      toast.error(t('deals.messages.error'));
    }
  };

  const handleDelete = async (id: string) => {
    setDealToDelete(id);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (dealToDelete) {
      try {
        await deleteDeal(dealToDelete);
        toast.success(t('deals.messages.deleted'));
      } catch (error) {
        console.error('Error deleting deal:', error);
        toast.error(t('deals.messages.error'));
      }
      setIsConfirmDeleteOpen(false);
      setDealToDelete(null);
    }
  };

  const handleView = (deal: Deal) => {
    setViewDeal(deal);
    setIsViewModalOpen(true);
  };

  const handleNewDeal = () => {
    setSelectedDeal(null);
    setIsFormOpen(true);
    if (onFormOpened) {
      onFormOpened();
    }
  };

  const handleUserAssignment = async (dealId: string, newUserId: string) => {
    try {
      setEditingUserId(dealId);
      await updateDeal(dealId, { assignedUserId: newUserId });
      toast.success(t('deals.messages.userAssigned'));
    } catch (error) {
      console.error('Error updating user assignment:', error);
      toast.error(t('deals.messages.error'));
    } finally {
      setEditingUserId(null);
      setUserDropdownOpen(null);
    }
  };

  // Effect to handle trigger from parent component
  React.useEffect(() => {
    if (triggerNewForm) {
      handleNewDeal();
    }
  }, [triggerNewForm]);

  // Effect to handle FAB trigger for new deal
  React.useEffect(() => {
    const handleFABNewDeal = () => {
      handleNewDeal();
    };

    window.addEventListener('openDealForm', handleFABNewDeal);
    return () => {
      window.removeEventListener('openDealForm', handleFABNewDeal);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading || customersLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4">
      <div className="w-full">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('common.placeholders.deals')}
        />
      </div>

      {/* Modern Filters - Chip Style - Horizontal Layout */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        {/* Filter Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Szűrők</h3>
            {hasActiveFilters && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200">
                {[
                  selectedTypes.length > 0 && `${selectedTypes.length} típus`,
                  selectedStatuses.length > 0 && `${selectedStatuses.length} állapot`,
                  selectedTags.length > 0 && `${selectedTags.length} tag`,
                  selectedUsers.length > 0 && `${selectedUsers.length} felhasználó`,
                  (dateFrom || dateTo) && 'Dátum'
                ].filter(Boolean).length} aktív
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
            >
              <XMarkIcon className="h-3 w-3 mr-1" />
              Törlés
            </button>
          )}
        </div>

        {/* All Filters - Mobile optimized */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* Date Range */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 hidden sm:inline">Dátum:</span>
            <div className="flex space-x-0.5 sm:space-x-1">
              <button
                onClick={() => handleDatePreset('')}
                className={`px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  datePreset === ''
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <span className="hidden sm:inline">Minden</span>
                <span className="sm:hidden">Mind</span>
              </button>
              <button
                onClick={() => handleDatePreset('today')}
                className={`px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  datePreset === 'today'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                Mai
              </button>
              <button
                onClick={() => handleDatePreset('thisWeek')}
                className={`px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  datePreset === 'thisWeek'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
              >
                <span className="hidden sm:inline">E heti</span>
                <span className="sm:hidden">Hét</span>
              </button>
              <button
                onClick={() => handleDatePreset('lastWeek')}
                className={`px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  datePreset === 'lastWeek'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                }`}
              >
                <span className="hidden sm:inline">Múlt heti</span>
                <span className="sm:hidden">M.hét</span>
              </button>
              <button
                onClick={() => handleDatePreset('thisMonth')}
                className={`px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  datePreset === 'thisMonth'
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                }`}
              >
                <span className="hidden sm:inline">E hónapi</span>
                <span className="sm:hidden">Hónap</span>
              </button>
              <button
                onClick={() => handleDatePreset('custom')}
                className={`px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  datePreset === 'custom'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                }`}
              >
                <span className="hidden sm:inline">Egyedi</span>
                <span className="sm:hidden">+</span>
              </button>
            </div>
            
            {/* Custom date inputs - only show when custom is selected */}
            {datePreset === 'custom' && (
              <div className="flex items-center space-x-2 ml-2">
            <input
              type="date"
              value={dateFrom}
                  onChange={(e) => handleManualDateChange('from', e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 [color-scheme:light] dark:[color-scheme:dark]"
              placeholder="Tól"
            />
            <span className="text-gray-400 dark:text-gray-500 text-xs">–</span>
            <input
              type="date"
              value={dateTo}
                  onChange={(e) => handleManualDateChange('to', e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 [color-scheme:light] dark:[color-scheme:dark]"
              placeholder="Ig"
            />
              </div>
            )}
          </div>

          {/* Type Filters */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Típus:</span>
            <div className="flex space-x-1">
              <button
                onClick={() => setSelectedTypes([])}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedTypes.length === 0
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Minden
              </button>
              <button
                onClick={() => handleTypeFilter('phone')}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedTypes.includes('phone')
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                <PhoneIcon className="w-3 h-3 mr-1" />
                Telefon
              </button>
              <button
                onClick={() => handleTypeFilter('email')}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedTypes.includes('email')
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                }`}
              >
                <EnvelopeIcon className="w-3 h-3 mr-1" />
                Email
              </button>
              <button
                onClick={() => handleTypeFilter('sms')}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedTypes.includes('sms')
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
              >
                <ChatBubbleLeftRightIcon className="w-3 h-3 mr-1" />
                SMS
              </button>
            </div>
          </div>

          {/* Status Filters */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Állapot:</span>
            <div className="flex space-x-1">
              <button
                onClick={() => setSelectedStatuses([])}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedStatuses.length === 0
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Minden
              </button>
              <button
                onClick={() => handleStatusFilter('pending')}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedStatuses.includes('pending')
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                }`}
              >
                <ClockIcon className="w-3 h-3 mr-1" />
                Folyamatban
              </button>
              <button
                onClick={() => handleStatusFilter('completed')}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedStatuses.includes('completed')
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
              >
                <CheckIcon className="w-3 h-3 mr-1" />
                Befejezve
              </button>
              <button
                onClick={() => handleStatusFilter('rejected')}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedStatuses.includes('rejected')
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                }`}
              >
                <XMarkIcon className="w-3 h-3 mr-1" />
                Elutasítva
              </button>
            </div>
          </div>

          {/* User Filters */}
          {availableUsers.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Felelős:</span>
              <div className="flex space-x-1 flex-wrap gap-1">
                <button
                  onClick={() => setSelectedUsers([])}
                  className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                    selectedUsers.length === 0
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Minden
                </button>
                <button
                  onClick={() => handleUserFilter('unassigned')}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                    selectedUsers.includes('unassigned')
                      ? 'bg-gray-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <UserIcon className="w-3 h-3 mr-1" />
                  Nincs hozzárendelve
                </button>
                {availableUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleUserFilter(user.id)}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                      selectedUsers.includes(user.id)
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                    }`}
                  >
                    <UserIcon className="w-3 h-3 mr-1" />
                    {user.displayName || user.email}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tag Filters */}
          {getAllTags().length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Tag-ek:</span>
              <div className="flex space-x-1 flex-wrap gap-1">
                <button
                  onClick={() => setSelectedTags([])}
                  className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                    selectedTags.length === 0
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Minden
                </button>
                {getAllTags().map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagFilter(tag)}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-all ${
                      selectedTags.includes(tag)
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Summary */}
      {deals.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="text-sm text-blue-800 dark:text-blue-200">
            {filteredDeals.length === deals.length
              ? `Összesen ${deals.length} ügylet`
              : `${filteredDeals.length} ügylet megjelenítve ${deals.length}-ból`
            }
            {hasActiveFilters && (
              <span className="ml-2 text-blue-600 dark:text-blue-400">(szűrők alkalmazva)</span>
            )}
          </div>
        </div>
      )}

      {filteredDeals.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg text-center shadow-sm border border-gray-200 dark:border-gray-700">
          {deals.length === 0 ? (
            <div>
              <p className="text-gray-500 dark:text-gray-400 mb-2">Még nincsenek ügyletei</p>
              <button
                onClick={handleNewDeal}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Hozzon létre egyet most!
              </button>
            </div>
          ) : (
            <div>
              <p className="text-gray-500 dark:text-gray-400 mb-2">Nincs a szűrőknek megfelelő ügylet</p>
              <button
                onClick={clearFilters}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Szűrők törlése
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto overflow-y-visible">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('deals.fields.type')}
                    </th>
                                         {!customerId && (
                       <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                         {t('deals.fields.customer')}
                       </th>
                     )}
                     <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                       {t('deals.fields.assignedUser')}
                     </th>
                     {!customerId && (
                       <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                         Telefon
                       </th>
                     )}
                     {!customerId && (
                       <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                         Email
                       </th>
                     )}
                                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                       {t('deals.fields.description')}
                     </th>
                     <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                       Tag-ek
                     </th>
                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      {t('deals.fields.date')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      {t('deals.fields.followUpDate')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('deals.fields.status')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {currentDeals.map((deal) => {
                    const statusColor = deal.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                      deal.status === 'rejected' ? 'text-red-600 dark:text-red-400' :
                        'text-yellow-600 dark:text-yellow-400';
                    const StatusIcon = deal.status === 'completed' ? CheckIcon :
                      deal.status === 'rejected' ? TrashIcon :
                        ClockIcon;
                    const borderColor = deal.status === 'completed'
                      ? 'bg-green-400'
                      : deal.status === 'rejected'
                        ? 'bg-red-400'
                        : 'bg-yellow-400';

                    return (
                      <tr key={deal.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="pl-3 pr-3 py-3 relative">
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${borderColor}`}></div>
                          <div className="flex items-center ml-1">
                            {deal.type === 'phone' ? (
                              <div className="p-1.5 rounded bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/20 dark:to-blue-800/20 flex-shrink-0">
                                <PhoneIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              </div>
                            ) : deal.type === 'email' ? (
                              <div className="p-1.5 rounded bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900/20 dark:to-indigo-800/20 flex-shrink-0">
                                <EnvelopeIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                              </div>
                            ) : (
                              <div className="p-1.5 rounded bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/20 dark:to-green-800/20 flex-shrink-0">
                                <ChatBubbleLeftRightIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                              </div>
                            )}
                            <span className="text-sm text-gray-700 dark:text-gray-300 ml-2">
                              {t(`deals.types.${deal.type}`)}
                              </span>
                          </div>
                        </td>
                        {!customerId && (
                          <td className="px-3 py-3">
                            <div className="flex items-center">
                              {customers?.find(c => c.id === deal.customerId)?.type === 'organization' ? (
                                <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/20 dark:border-blue-500/30 max-w-[150px] overflow-hidden">
                                  <BuildingOffice2Icon className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{getCustomerName(deal.customerId)}</span>
                                </div>
                              ) : (
                                <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/10 dark:bg-green-500/20 text-green-600 dark:text-green-400 border border-green-500/20 dark:border-green-500/30 max-w-[150px] overflow-hidden">
                                  <UserIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{getCustomerName(deal.customerId)}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-3 relative">
                          <div className="flex items-center">
                            {editingUserId === deal.id ? (
                              // Loading state
                              <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                                <div className="animate-spin w-3 h-3 border border-gray-400 border-t-transparent rounded-full mr-1"></div>
                                <span>Mentés...</span>
                              </div>
                            ) : (
                              <div className="relative" ref={dropdownRef}>
                                <button
                                  onClick={() => setUserDropdownOpen(userDropdownOpen === deal.id ? null : deal.id)}
                                  className="group"
                                  data-deal-id={deal.id}
                                >
                                  {deal.assignedUserId ? (
                                    (() => {
                                      const userName = getUserName(deal.assignedUserId);
                                      const isFromOtherOrg = userName.includes(`(${t('deals.fields.otherOrganization')})`);
                                      return (
                                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium max-w-[150px] overflow-hidden transition-all hover:scale-105 cursor-pointer ${
                                          isFromOtherOrg 
                                            ? 'bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-500/20 dark:border-orange-500/30 hover:bg-orange-500/20 dark:hover:bg-orange-500/30'
                                            : 'bg-purple-500/10 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 border border-purple-500/20 dark:border-purple-500/30 hover:bg-purple-500/20 dark:hover:bg-purple-500/30'
                                        }`}>
                                          <UserIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                                          <span className="truncate">{userName}</span>
                                          <ChevronDownIcon className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-all">
                                      <UserIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                                      <span>{t('deals.fields.noAssignedUser')}</span>
                                      <ChevronDownIcon className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  )}
                                </button>

                                {/* Dropdown Menu */}
                                {userDropdownOpen === deal.id && (
                                  <div className="absolute z-[9999] left-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg"
                                    style={{
                                      left: '0'
                                    }}>
                                    <div className="py-1">
                                      {/* Nincs hozzárendelve opció */}
                                      <button
                                        onClick={() => handleUserAssignment(deal.id, '')}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                          !deal.assignedUserId ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-700 dark:text-gray-300'
                                        }`}
                                      >
                                        <div className="flex items-center">
                                          <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                                          {t('deals.fields.noAssignedUser')}
                                        </div>
                                      </button>
                                      
                                      {/* Felhasználók */}
                                      {availableUsers.map(user => (
                                        <button
                                          key={user.id}
                                          onClick={() => handleUserAssignment(deal.id, user.id)}
                                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                            deal.assignedUserId === user.id ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                                          }`}
                                        >
                                          <div className="flex items-center">
                                            <UserIcon className="w-4 h-4 mr-2 text-purple-500" />
                                            <span className="truncate">{user.displayName || user.email}</span>
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        {!customerId && (
                          <td className="px-3 py-3">
                            <div className="flex items-center">
                              {getCustomerPhone(deal.customerId) ? (
                                <a 
                                  href={`tel:${getCustomerPhone(deal.customerId)}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-600 transition-colors cursor-pointer"
                                  title={`Hívás: ${getCustomerPhone(deal.customerId)}`}
                                >
                                  <PhoneIcon className="w-3 h-3 mr-1 flex-shrink-0 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400" />
                                  <span>{getCustomerPhone(deal.customerId)}</span>
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                              )}
                            </div>
                          </td>
                        )}
                        {!customerId && (
                          <td className="px-3 py-3">
                            <div className="flex items-center">
                              {getCustomerEmail(deal.customerId) ? (
                                <a 
                                  href={`mailto:${getCustomerEmail(deal.customerId)}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-400 hover:border-green-300 dark:hover:border-green-600 transition-colors cursor-pointer max-w-[150px] overflow-hidden"
                                  title={`Email küldése: ${getCustomerEmail(deal.customerId)}`}
                                >
                                  <EnvelopeIcon className="w-3 h-3 mr-1 flex-shrink-0 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400" />
                                  <span className="truncate">{getCustomerEmail(deal.customerId)}</span>
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <div className="space-y-1 min-w-0">
                            {deal.type === 'email' && deal.subject && (
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs">
                                {deal.subject}
                              </p>
                            )}
                                                         <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                               {deal.description}
                             </p>
                           </div>
                         </td>
                         <td className="px-3 py-3">
                           <div className="flex flex-wrap gap-1">
                             {deal.tags && deal.tags.length > 0 ? (
                               deal.tags.map(tag => (
                                 <span
                                   key={tag}
                                   className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                                 >
                                   #{tag}
                                 </span>
                               ))
                             ) : (
                               <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                             )}
                           </div>
                         </td>
                         <td className="px-3 py-3 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center text-sm text-gray-700 dark:text-gray-300">
                            <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-gray-500 dark:text-gray-400" />
                            {new Date(deal.date).toLocaleDateString('hu-HU')}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          {deal.followUpDate ? (
                            <div className="flex items-center justify-center text-sm text-yellow-600 dark:text-yellow-400">
                              <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                              {new Date(deal.followUpDate).toLocaleString('hu-HU', {
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500 dark:text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${statusColor} bg-opacity-20 ${deal.status === 'completed' ? 'bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
                            deal.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                              'bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                            }`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {t(`deals.status.${deal.status}`)}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end space-x-2">
                            <Tooltip content={t('common.actionsObject.view')}>
                              <button
                                onClick={() => handleView(deal)}
                                className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t('common.actionsObject.edit')}>
                              <button
                                onClick={() => {
                                  setSelectedDeal(deal);
                                  setIsFormOpen(true);
                                }}
                                className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t('common.actionsObject.delete')}>
                              <button
                                onClick={() => handleDelete(deal.id)}
                                className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination integrated within the table container */}
            {filteredDeals.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-700">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredDeals.length}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            )}
          </div>

          {/* Tablet view - Compact cards */}
          <div className="hidden md:block lg:hidden">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
              {currentDeals.map(deal => {
              const statusColor = deal.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                deal.status === 'rejected' ? 'text-red-600 dark:text-red-400' :
                  'text-yellow-600 dark:text-yellow-400';
              const StatusIcon = deal.status === 'completed' ? CheckIcon :
                deal.status === 'rejected' ? TrashIcon :
                  ClockIcon;
              const borderColor = deal.status === 'completed'
                ? 'border-l-green-400'
                : deal.status === 'rejected'
                  ? 'border-l-red-400'
                  : 'border-l-yellow-400';

              return (
                  <div key={deal.id} className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${borderColor} hover:shadow-md transition-shadow`}>
                    {/* Header */}
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center">
                        {deal.type === 'phone' ? (
                          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 mr-3">
                            <PhoneIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                        ) : deal.type === 'email' ? (
                          <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 mr-3">
                            <EnvelopeIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                          </div>
                        ) : (
                          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 mr-3">
                            <ChatBubbleLeftRightIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t(`deals.types.${deal.type}`)}</span>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColor} ${deal.status === 'completed' ? 'bg-green-100 dark:bg-green-900/20' :
                        deal.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/20' :
                          'bg-yellow-100 dark:bg-yellow-900/20'
                        }`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {t(`deals.status.${deal.status}`)}
                      </span>
                    </div>

                    {/* Content grid */}
                    <div className="grid grid-cols-1 gap-3">
                      {/* Customer & User row */}
                      {!customerId && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            {customers?.find(c => c.id === deal.customerId)?.type === 'organization' ? (
                              <div className="inline-flex items-center px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-sm">
                                <BuildingOffice2Icon className="w-4 h-4 mr-2" />
                                <span className="truncate max-w-[150px]">{getCustomerName(deal.customerId)}</span>
                        </div>
                      ) : (
                              <div className="inline-flex items-center px-2 py-1 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm">
                                <UserIcon className="w-4 h-4 mr-2" />
                                <span className="truncate max-w-[150px]">{getCustomerName(deal.customerId)}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Contact buttons */}
                          <div className="flex gap-2">
                            {getCustomerPhone(deal.customerId) && (
                              <a href={`tel:${getCustomerPhone(deal.customerId)}`} className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                                <PhoneIcon className="w-4 h-4" />
                              </a>
                            )}
                            {getCustomerEmail(deal.customerId) && (
                              <a href={`mailto:${getCustomerEmail(deal.customerId)}`} className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors">
                                <EnvelopeIcon className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Assigned User */}
                        <div className="flex items-center">
                        {deal.assignedUserId ? (
                          (() => {
                            const userName = getUserName(deal.assignedUserId);
                            const isFromOtherOrg = userName.includes(`(${t('deals.fields.otherOrganization')})`);
                            return (
                              <div className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                                isFromOtherOrg 
                                  ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                  : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                              }`}>
                                <UserIcon className="w-4 h-4 mr-2" />
                                <span className="truncate max-w-[150px]">{userName}</span>
                          </div>
                            );
                          })()
                        ) : (
                          <div className="inline-flex items-center px-2 py-1 rounded bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm">
                            <UserIcon className="w-4 h-4 mr-2" />
                            <span>{t('deals.fields.noAssignedUser')}</span>
                        </div>
                      )}
                    </div>

                      {/* Content */}
                      {deal.type === 'email' && deal.subject && (
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                          {deal.subject}
                        </p>
                      )}
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                        {deal.description}
                      </p>

                      {/* Tags */}
                      {deal.tags && deal.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {deal.tags.slice(0, 4).map(tag => (
                            <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                              #{tag}
                            </span>
                          ))}
                          {deal.tags.length > 4 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">+{deal.tags.length - 4}</span>
                          )}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            <span>{new Date(deal.date).toLocaleDateString('hu-HU')}</span>
                          </div>
                          {deal.followUpDate && (
                            <div className="flex items-center text-amber-600 dark:text-amber-400">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              <span>{new Date(deal.followUpDate).toLocaleDateString('hu-HU')}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-2">
                          <button onClick={() => handleView(deal)} className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all">
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => { setSelectedDeal(deal); setIsFormOpen(true); }} className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all">
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDelete(deal.id)} className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination for tablet */}
            {filteredDeals.length > 0 && (
              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredDeals.length}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            )}
          </div>

          {/* Extra small mobile view (iPhone SE, etc.) */}
          <div className="block sm:hidden space-y-2 mb-4">
            {currentDeals.map(deal => {
              const statusColor = deal.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                deal.status === 'rejected' ? 'text-red-600 dark:text-red-400' :
                  'text-yellow-600 dark:text-yellow-400';
              const StatusIcon = deal.status === 'completed' ? CheckIcon :
                deal.status === 'rejected' ? TrashIcon :
                  ClockIcon;
              const borderColor = deal.status === 'completed'
                ? 'border-l-green-400'
                : deal.status === 'rejected'
                  ? 'border-l-red-400'
                  : 'border-l-yellow-400';

              return (
                <div key={deal.id} className={`bg-white dark:bg-gray-800 rounded-lg p-2.5 shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${borderColor} active:scale-95 transition-transform duration-150`}>
                  {/* Ultra compact header */}
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="flex items-center">
                      {deal.type === 'phone' ? (
                        <div className="p-1 rounded bg-blue-100 dark:bg-blue-900/30 mr-1.5">
                          <PhoneIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                        </div>
                      ) : deal.type === 'email' ? (
                        <div className="p-1 rounded bg-indigo-100 dark:bg-indigo-900/30 mr-1.5">
                          <EnvelopeIcon className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                        </div>
                      ) : (
                        <div className="p-1 rounded bg-green-100 dark:bg-green-900/30 mr-1.5">
                          <ChatBubbleLeftRightIcon className="h-3 w-3 text-green-600 dark:text-green-400" />
                        </div>
                      )}
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t(`deals.types.${deal.type}`)}</span>
                    </div>
                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${statusColor} ${deal.status === 'completed' ? 'bg-green-100 dark:bg-green-900/20' :
                      deal.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/20' :
                        'bg-yellow-100 dark:bg-yellow-900/20'
                      }`}>
                      <StatusIcon className="h-2 w-2 mr-0.5" />
                      {t(`deals.status.${deal.status}`).substring(0, 4)}
                    </span>
                  </div>

                  {/* Ultra compact content */}
                  <div className="space-y-1.5">
                    {/* Customer & User in same row */}
                    <div className="flex items-center justify-between text-xs">
                      {!customerId && (
                        <div className="flex items-center min-w-0 flex-1 mr-2">
                          {customers?.find(c => c.id === deal.customerId)?.type === 'organization' ? (
                            <div className="inline-flex items-center px-1 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 min-w-0">
                              <BuildingOffice2Icon className="w-2 h-2 mr-1 flex-shrink-0" />
                              <span className="truncate text-xs max-w-[80px]">{getCustomerName(deal.customerId)}</span>
                            </div>
                          ) : (
                            <div className="inline-flex items-center px-1 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 min-w-0">
                              <UserIcon className="w-2 h-2 mr-1 flex-shrink-0" />
                              <span className="truncate text-xs max-w-[80px]">{getCustomerName(deal.customerId)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Assigned User - Ultra compact */}
                      <div className="flex items-center min-w-0">
                        {deal.assignedUserId ? (
                          (() => {
                            const userName = getUserName(deal.assignedUserId);
                            const isFromOtherOrg = userName.includes(`(${t('deals.fields.otherOrganization')})`);
                            return (
                              <div className={`inline-flex items-center px-1 py-0.5 rounded text-xs min-w-0 ${
                                isFromOtherOrg 
                                  ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                  : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                              }`}>
                                <UserIcon className="w-2 h-2 mr-1 flex-shrink-0" />
                                <span className="truncate max-w-[60px]">{userName}</span>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="inline-flex items-center px-1 py-0.5 rounded bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs">
                            <UserIcon className="w-2 h-2 mr-1" />
                            <span className="text-xs">-</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Content - Ultra compact */}
                    <div>
                      {deal.type === 'email' && deal.subject && (
                        <p className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-1 mb-0.5">
                          {deal.subject}
                        </p>
                      )}
                      <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-1">
                        {deal.description}
                      </p>
                    </div>

                    {/* Footer row - Ultra compact */}
                    <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{new Date(deal.date).toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })}</span>
                        {deal.followUpDate && (
                          <span className="text-amber-600 dark:text-amber-400">
                            ⏰{new Date(deal.followUpDate).toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })}
                          </span>
                        )}
                      </div>

                      {/* Contact + Actions - Ultra compact */}
                      <div className="flex items-center space-x-1">
                        {!customerId && getCustomerPhone(deal.customerId) && (
                          <a href={`tel:${getCustomerPhone(deal.customerId)}`} className="p-1 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                            <PhoneIcon className="w-3 h-3" />
                          </a>
                        )}
                        {!customerId && getCustomerEmail(deal.customerId) && (
                          <a href={`mailto:${getCustomerEmail(deal.customerId)}`} className="p-1 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                            <EnvelopeIcon className="w-3 h-3" />
                          </a>
                        )}
                        <button onClick={() => handleView(deal)} className="p-1 rounded text-gray-400 hover:text-blue-600 active:scale-95">
                          <EyeIcon className="h-3 w-3" />
                        </button>
                        <button onClick={() => { setSelectedDeal(deal); setIsFormOpen(true); }} className="p-1 rounded text-gray-400 hover:text-primary-600 active:scale-95">
                          <PencilSquareIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Small mobile view (iPhone 12, 13, etc.) */}
          <div className="hidden sm:block md:hidden space-y-3 mb-4 px-1">
            {filteredDeals.map(deal => {
              const statusColor = deal.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                deal.status === 'rejected' ? 'text-red-600 dark:text-red-400' :
                  'text-yellow-600 dark:text-yellow-400';
              const StatusIcon = deal.status === 'completed' ? CheckIcon :
                deal.status === 'rejected' ? TrashIcon :
                  ClockIcon;
              const borderColor = deal.status === 'completed'
                ? 'border-l-green-400'
                : deal.status === 'rejected'
                  ? 'border-l-red-400'
                  : 'border-l-yellow-400';

              return (
                <div key={deal.id} className={`bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 ${borderColor} active:scale-95 transition-transform duration-150`}>
                  {/* Header - Compact */}
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      {deal.type === 'phone' ? (
                        <div className="p-1 rounded-md bg-blue-100 dark:bg-blue-900/30 mr-2">
                          <PhoneIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                        </div>
                      ) : deal.type === 'email' ? (
                        <div className="p-1 rounded-md bg-indigo-100 dark:bg-indigo-900/30 mr-2">
                          <EnvelopeIcon className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                      ) : (
                        <div className="p-1 rounded-md bg-green-100 dark:bg-green-900/30 mr-2">
                          <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                        </div>
                      )}
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t(`deals.types.${deal.type}`)}</span>
                    </div>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${statusColor} ${deal.status === 'completed' ? 'bg-green-100 dark:bg-green-900/20' :
                      deal.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/20' :
                        'bg-yellow-100 dark:bg-yellow-900/20'
                      }`}>
                      <StatusIcon className="h-2.5 w-2.5 mr-1" />
                      {t(`deals.status.${deal.status}`)}
                    </span>
                  </div>

                  {/* Compact Information */}
                  <div className="space-y-2">
                    {/* Customer & Assignee Row */}
                    <div className="flex flex-wrap gap-1.5 text-xs">
                  {!customerId && (
                        <div className="flex items-center">
                      {customers?.find(c => c.id === deal.customerId)?.type === 'organization' ? (
                            <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                              <BuildingOffice2Icon className="w-2.5 h-2.5 mr-1" />
                              <span className="truncate max-w-[120px]">{getCustomerName(deal.customerId)}</span>
                            </div>
                      ) : (
                            <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                              <UserIcon className="w-2.5 h-2.5 mr-1" />
                              <span className="truncate max-w-[120px]">{getCustomerName(deal.customerId)}</span>
                            </div>
                      )}
                        </div>
                      )}
                      
                      {/* Assigned User - Compact */}
                      <div className="flex items-center">
                        {deal.assignedUserId ? (
                          (() => {
                            const userName = getUserName(deal.assignedUserId);
                            const isFromOtherOrg = userName.includes(`(${t('deals.fields.otherOrganization')})`);
                            return (
                              <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${
                                isFromOtherOrg 
                                  ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                  : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                              }`}>
                                <UserIcon className="w-2.5 h-2.5 mr-1" />
                                <span className="truncate max-w-[100px]">{userName}</span>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs">
                            <UserIcon className="w-2.5 h-2.5 mr-1" />
                            <span className="text-xs">Nincs</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact Info - Compact */}
                    {!customerId && (getCustomerPhone(deal.customerId) || getCustomerEmail(deal.customerId)) && (
                      <div className="flex gap-1.5">
                          {getCustomerPhone(deal.customerId) && (
                            <a 
                              href={`tel:${getCustomerPhone(deal.customerId)}`}
                            className="inline-flex items-center px-1.5 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 active:bg-blue-100 dark:active:bg-blue-900/40 transition-colors"
                            >
                            <PhoneIcon className="w-3 h-3" />
                            </a>
                          )}
                          {getCustomerEmail(deal.customerId) && (
                            <a 
                              href={`mailto:${getCustomerEmail(deal.customerId)}`}
                            className="inline-flex items-center px-1.5 py-1 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 active:bg-green-100 dark:active:bg-green-900/40 transition-colors"
                            >
                            <EnvelopeIcon className="w-3 h-3" />
                            </a>
                          )}
                    </div>
                  )}

                    {/* Content */}
                    <div className="space-y-1">
                  {deal.type === 'email' && deal.subject && (
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                        {deal.subject}
                      </p>
                  )}
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                      {deal.description}
                    </p>
                    </div>
                    
                    {/* Tags - Compact */}
                    {deal.tags && deal.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {deal.tags.slice(0, 3).map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                          >
                            #{tag}
                          </span>
                        ))}
                        {deal.tags.length > 3 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">+{deal.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer - Compact */}
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        <span>{new Date(deal.date).toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })}</span>
                    </div>
                    {deal.followUpDate && (
                        <div className="flex items-center text-amber-600 dark:text-amber-400">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          <span>{new Date(deal.followUpDate).toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })}</span>
                      </div>
                    )}
                    </div>

                    {/* Actions - Touch-friendly */}
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleView(deal)}
                        className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 active:scale-95 transition-all duration-150"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDeal(deal);
                          setIsFormOpen(true);
                        }}
                        className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 active:scale-95 transition-all duration-150"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(deal.id)}
                        className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 active:scale-95 transition-all duration-150"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination for all mobile views */}
          {filteredDeals.length > 0 && (
            <div className="md:hidden mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filteredDeals.length}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </>
      )}

      {/* Deal Form */}
      <DealForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedDeal(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedDeal || undefined}
        customerId={customerId}
      />

      {/* Modern View Deal Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Ügylet részletei"
        size="3xl"
        noPadding
      >
        {viewDeal && (() => {
          const statusMeta = viewDeal.status === 'completed'
            ? { cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', Icon: CheckIcon }
            : viewDeal.status === 'rejected'
            ? { cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300', Icon: XMarkIcon }
            : { cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', Icon: ClockIcon };
          const TypeIcon = viewDeal.type === 'phone' ? PhoneIcon : viewDeal.type === 'sms' ? ChatBubbleLeftRightIcon : EnvelopeIcon;
          const cust = customers?.find(c => c.id === viewDeal.customerId);
          const row = (label: string, value: React.ReactNode) => (
            <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
              <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</span>
              <span className="text-right font-medium text-gray-900 dark:text-gray-100 min-w-0">{value}</span>
            </div>
          );
          return (
          <div className="flex flex-col min-h-full">
            <div className="flex-1 px-4 sm:px-6 py-4 sm:py-5 space-y-4">
              {/* Fejléc */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
                  <TypeIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight truncate">
                    {viewDeal.type === 'email' && viewDeal.subject ? viewDeal.subject : t(`deals.types.${viewDeal.type}`)}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {format(new Date(viewDeal.date), 'yyyy. MMMM d. HH:mm', { locale: hu })}
                    {cust && <> · {getCustomerName(viewDeal.customerId)}</>}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs font-medium flex-shrink-0 ${statusMeta.cls}`}>
                  <statusMeta.Icon className="h-3.5 w-3.5" />
                  {t(`deals.status.${viewDeal.status}`)}
                </span>
              </div>

              {/* Adatok */}
              <div className="crm-card px-4 py-1 divide-y divide-[var(--crm-border)]">
                {row('Ügyfél', (
                  <span className="inline-flex items-center gap-1.5">
                    {cust?.type === 'organization' ? <BuildingOffice2Icon className="h-3.5 w-3.5 text-gray-400" /> : <UserIcon className="h-3.5 w-3.5 text-gray-400" />}
                    {getCustomerName(viewDeal.customerId)}
                  </span>
                ))}
                {row('Típus', t(`deals.types.${viewDeal.type}`))}
                {viewDeal.duration ? row('Időtartam', `${viewDeal.duration} perc`) : null}
                {viewDeal.followUpDate ? row('Következő találkozó', format(new Date(viewDeal.followUpDate), 'yyyy. MM. dd. HH:mm', { locale: hu })) : null}
                {viewDeal.tags && viewDeal.tags.length > 0 ? row('Címkék', (
                  <span className="flex flex-wrap justify-end gap-1">
                    {viewDeal.tags.map(tag => <span key={tag} className="px-1.5 py-0.5 rounded text-[11px] bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300">{tag}</span>)}
                  </span>
                )) : null}
              </div>

              {/* Leírás / eredmény */}
              {viewDeal.description && (
                <div className="crm-card p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Leírás</div>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{viewDeal.description}</p>
                </div>
              )}
              {viewDeal.outcome && (
                <div className="crm-card p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1"><CheckIcon className="h-3.5 w-3.5 text-emerald-500" />Eredmény</div>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{viewDeal.outcome}</p>
                </div>
              )}

              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                Létrehozva: {format(new Date(viewDeal.createdAt), 'yyyy. MM. dd. HH:mm', { locale: hu })} · Módosítva: {format(new Date(viewDeal.updatedAt), 'yyyy. MM. dd. HH:mm', { locale: hu })}
              </p>
            </div>

            <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-4 sm:px-6 py-3 border-t border-[var(--crm-border)] bg-[var(--crm-surface)]">
              <button type="button" onClick={() => setIsViewModalOpen(false)} className="crm-btn crm-btn-secondary">Bezárás</button>
              <button
                type="button"
                onClick={() => { setIsViewModalOpen(false); setSelectedDeal(viewDeal); setIsFormOpen(true); }}
                className="crm-btn crm-btn-primary"
              >
                <PencilSquareIcon className="h-4 w-4" />
                Szerkesztés
              </button>
            </div>
          </div>
          );
        })()}
      </Modal>

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={t('deals.messages.confirmTitle')}
        deleteMessage={`${t('deals.messages.deleteQuestion')}`}
        deleteWarning={t('deals.messages.deleteWarning')}
      />
    </div>
  );
};

export default DealList; 